import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { QuoteRequestForm } from "@/components/site/QuoteRequestForm";
import {
  CheckCircle2,
  ShieldCheck,
  Zap,
  Award,
  MapPin,
  ArrowRight,
  Phone,
} from "lucide-react";

const SITE_URL = "https://elvion-group.nl";

const FAQS = [
  {
    q: "Wat kost het installeren van een laadpaal?",
    a: "Een complete installatie van een thuislaadpaal kost doorgaans € 1.200 – € 2.200, inclusief laadpaal, kabelwerk tot ~15 meter, een type B aardlekautomaat in de groepenkast en oplevering met meetrapport. Bij langere kabeltrajecten, doorvoeren door muren of een 3-fase aansluiting kan de prijs hoger uitvallen. Voor zakelijke locaties en VvE's maken we altijd een offerte op maat.",
  },
  {
    q: "Is er subsidie voor een laadpaal in 2026?",
    a: "Voor particulieren is er geen landelijke aanschafsubsidie meer, maar veel gemeenten en netbeheerders bieden tijdelijke regelingen (bijvoorbeeld voor VvE's of bij een openbare laadpaal in de straat). Zakelijke rijders kunnen vaak gebruik maken van de MIA/Vamil-regeling. Wij checken bij de offerte welke regelingen voor jouw situatie gelden.",
  },
  {
    q: "Hoe lang duurt de installatie?",
    a: "Een standaard installatie aan huis duurt 3 tot 5 uur. We voeren eerst een inmeetbezoek (of digitale check) uit, plaatsen daarna de laadpaal, leggen de kabels naar de meterkast en sluiten alles veilig aan op een eigen groep met type B ALB.",
  },
  {
    q: "Moet mijn groepenkast worden aangepast?",
    a: "Vaak wel. Een laadpaal heeft een eigen groep nodig met een type B aardlekautomaat. Bij een oudere groepenkast met draadzekeringen of weinig ruimte adviseren we eerst een vervanging. Dat nemen we standaard mee in ons advies.",
  },
  {
    q: "Werken jullie ook voor bedrijven en VvE's?",
    a: "Ja. We installeren losse laadpalen, laadpleinen en dynamische load-balancing voor kantoren, bedrijfspanden en VvE-parkeergarages. Inclusief backoffice-koppeling en facturatie van laadsessies.",
  },
  {
    q: "Welke garantie krijg ik?",
    a: "10 jaar garantie op onze installatie en de fabrieksgarantie op de laadpaal zelf (meestal 3 jaar, uitbreidbaar tot 5). Alles wordt opgeleverd met een NEN1010-meetrapport.",
  },
];

export const Route = createFileRoute("/laadpaal-installateur")({
  head: () => {
    const url = `${SITE_URL}/laadpaal-installateur`;
    const title =
      "Laadpaal installateur — erkend, 10 jaar garantie | Elvion";
    const description =
      "Laadpaal laten installeren door een erkende installateur. Thuis, zakelijk of VvE. NEN1010-gecertificeerd, 10 jaar garantie, vaste prijs binnen 24 uur.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "nl_NL" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: LaadpaalInstallateurPage,
});

const TRUST_STRIP = [
  { icon: ShieldCheck, label: "10 jaar garantie" },
  { icon: Zap, label: "Type B ALB standaard" },
  { icon: Award, label: "NEN1010 & InstallQ" },
  { icon: MapPin, label: "Heel Nederland" },
];

const USPS = [
  "Erkende installateurs (NEN1010 & InstallQ)",
  "Alle merken: Alfen, Wallbox, EVBox, Easee, Zaptec",
  "Inclusief eigen groep met type B ALB",
  "Vaste prijs binnen 24 uur — geen verrassingen",
  "Particulier, zakelijk en VvE",
];

function LaadpaalInstallateurPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Trust strip */}
        <div className="border-b border-border bg-foreground text-background">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-3 text-sm sm:px-6 lg:px-8">
            <a
              href="tel:0850806695"
              className="flex items-center gap-2 font-semibold hover:text-orange"
            >
              <Phone className="h-4 w-4" />
              085 080 66 95
            </a>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {TRUST_STRIP.map((t) => (
                <div key={t.label} className="flex items-center gap-1.5">
                  <t.icon className="h-4 w-4 text-orange" />
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hero with form */}
        <section id="top" className="relative overflow-hidden bg-gradient-to-br from-[oklch(0.25_0.04_240)] via-[oklch(0.22_0.05_250)] to-[oklch(0.18_0.04_260)] text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,oklch(0.45_0.18_240/0.4),transparent_60%)]" />
          <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_minmax(380px,440px)] lg:gap-16 lg:px-8 lg:py-20">
            <div>
              <h1 className="text-4xl font-extrabold leading-tight tracking-tight md:text-5xl lg:text-6xl">
                Laadpaal installateur
                <br />
                voor thuis, zakelijk &amp; VvE
              </h1>
              <p className="mt-5 max-w-xl text-base text-white/80 md:text-lg">
                Erkende installateurs plaatsen jouw laadpaal veilig en
                toekomstbestendig — inclusief eigen groep, type B ALB en
                NEN1010-meetrapport. Vaste prijs binnen 24 uur.
              </p>

              <ul className="mt-8 space-y-3 text-base md:text-lg">
                {USPS.map((u) => (
                  <li key={u} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                    <span>{u}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <Link
                  to="/gids-laadpaal-kosten"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white underline-offset-4 hover:underline"
                >
                  Bekijk eerst de complete kostengids
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:pt-2">
              <QuoteRequestForm
                service="laadpaal"
                title="Vaste prijs binnen 24 uur"
                subtitle="Vul je gegevens in. We bellen je terug voor een gratis advies."
              />
            </div>
          </div>
        </section>

        {/* Korte uitleg */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-3">
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                Hoe werkt het?
              </h2>
              <p className="mt-3 text-muted-foreground">
                Na je aanvraag bellen we binnen één werkdag voor een korte
                check: type auto, gewenste laadpaal, afstand tot de meterkast
                en de staat van je groepenkast. Je krijgt een vaste prijs —
                geen meerwerk achteraf.
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                Wat krijg je bij ons?
              </h2>
              <ul className="mt-3 space-y-2 text-foreground">
                {[
                  "Inmeetbezoek en advies op locatie",
                  "Laadpaal van A-merk naar keuze",
                  "Eigen groep met type B aardlekautomaat",
                  "Kabelwerk tot ~15 meter inbegrepen",
                  "NEN1010-meetrapport bij oplevering",
                  "10 jaar garantie op onze installatie",
                ].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
                    {i}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                Voor wie?
              </h2>
              <p className="mt-3 text-muted-foreground">
                Particulieren met een eigen oprit, bedrijven die hun
                wagenpark willen elektrificeren en VvE's die een laadplein
                in de parkeergarage willen — inclusief dynamische
                load-balancing en facturatie van laadsessies.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-border bg-muted/30">
          <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">
              Veelgestelde vragen
            </h2>
            <p className="mt-2 text-muted-foreground">
              Over kosten, subsidie en de installatie zelf.
            </p>

            <div className="mt-8 space-y-3">
              {FAQS.map((f) => (
                <details
                  key={f.q}
                  className="group rounded-lg border border-border bg-background p-5 open:shadow-sm"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold text-foreground">
                    {f.q}
                    <ArrowRight className="h-4 w-4 shrink-0 text-orange transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>

            <div className="mt-10 rounded-xl border border-border bg-background p-6 text-center">
              <p className="text-base text-foreground">
                Nog vragen? Bel ons direct of vraag een vrijblijvende offerte
                aan.
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <a
                  href="tel:0850806695"
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
                >
                  <Phone className="h-4 w-4" />
                  085 080 66 95
                </a>
                <a
                  href="#top"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-muted"
                >
                  Naar offerteformulier
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
